import React, { useEffect, useState } from "react";
import { PageHeader, Btn, Modal, Select, Input, Table, Badge } from "@/components/UI";
import { api } from "@/lib/api";
import SafetyBindingBanner from "@/components/SafetyBindingBanner";
import { FileText, ShieldCheck, AlertTriangle, Sparkles, BookOpenCheck, Beaker, ClipboardCheck, ScrollText, Layers, CheckCircle2, XCircle, AlertCircle, Activity, Database, Plus, Save, Trash2, Pencil } from "lucide-react";

const STATUS_LEVEL = { green: "ok", yellow: "attention", orange: "critical", red: "missing", grey: "inactive", insufficient_data: "attention" };
const STATUS_LABEL = { green: "UYGUN", yellow: "DİKKAT", orange: "ÖNEMLİ", red: "UYGUN DEĞİL", grey: "DOĞRULANAMADI", insufficient_data: "EKSİK VERİ" };

const PIF_STATUS_LEVEL = {
  DRAFT: "attention", READY_FOR_REVIEW: "attention",
  APPROVED: "ok", SUPERSEDED: "inactive",
  BLOCKED: "missing", MISSING_INFORMATION: "critical",
};

const TABS = [
  { key: "compliance", label: "İhracat Uygunluk", icon: ShieldCheck },
  { key: "safety",     label: "Ürün Güvenlik Ön Değerlendirmesi", icon: Activity },
  { key: "pif",        label: "PIF Dosyaları",    icon: ClipboardCheck },
  { key: "sds",        label: "SDS Dosyaları",    icon: Beaker },
  { key: "sources",    label: "Mevzuat Kaynakları", icon: BookOpenCheck },
  { key: "dataset",    label: "Mevzuat Dataset Editörü", icon: Database, adminOnly: true },
];

export default function RegulatoryPage() {
  const [tab, setTab] = useState("compliance");
  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6">
      <PageHeader
        title="Regülasyon & İhracat"
        description="Ülke uygunluk analizi + PIF (Ürün Bilgi Dosyası) + SDS (Güvenlik Bilgi Formu)"
      />
      <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700 mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`reg-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 sm:px-4 py-2 flex items-center gap-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? "border-violet-500 text-violet-700 dark:text-violet-300"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}>
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "compliance" && <CompliancePane />}
      {tab === "safety" && <SafetyPane />}
      {tab === "dataset" && <DatasetEditorPane />}
      {tab === "pif" && <PifPane />}
      {tab === "sds" && <SdsPane />}
      {tab === "sources" && <SourcesPane />}
    </div>
  );
}

// ============================================================================
// COMPLIANCE (Phase 1 — restored inside tab)
// ============================================================================
function CompliancePane() {
  const [countries, setCountries] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [formulas, setFormulas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: "", product_id: "", formula_id: "", formula_revision: "", country: "EU", language: "tr", ai_model: "" });

  const load = async () => {
    const [cs, ai, a, forms, custs] = await Promise.all([
      api.get("/regulatory/countries"),
      api.get("/regulatory/ai-models").catch(() => []),
      api.get("/regulatory/analyses", { limit: 200 }).catch(() => []),
      api.get("/formulas").catch(() => []),
      api.get("/customers").catch(() => []),
    ]);
    setCountries(cs); setAiModels(ai); setAnalyses(a); setFormulas(forms); setCustomers(custs);
  };
  useEffect(() => { load(); }, []);

  const analyze = async () => {
    if (!f.formula_id) { alert("Formül seçin"); return; }
    setBusy(true);
    try {
      const res = await api.post("/regulatory/analyze", f);
      setDetail(res);
      setOpen(false);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Analiz hatası");
    } finally { setBusy(false); }
  };
  const dlPdf = async (row) => {
    await api.downloadFile(`/regulatory/analyses/${row.id}/pdf`, `regulatory_${row.analysis_no}.pdf`);
  };
  const openDetail = async (row) => setDetail(await api.get(`/regulatory/analyses/${row.id}`));

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Btn onClick={() => setOpen(true)} data-testid="new-reg-analysis"><ShieldCheck size={14}/> Yeni Uygunluk Analizi</Btn>
      </div>
      <Table testid="regulatory-analyses" onRowClick={openDetail}
        columns={[
          { label: "Analiz No", render: r => <span className="font-mono text-xs">{r.analysis_no}</span> },
          { label: "Ülke", render: r => <span className="font-semibold">{r.country}</span> },
          { label: "Ürün", render: r => r.formula_snapshot?.product_name || "—" },
          { label: "Formül", render: r => `${r.formula_snapshot?.formula_code || "—"} · ${r.formula_snapshot?.revision || ""}` },
          { label: "Müşteri", render: r => r.customer?.legal_name || "—" },
          { label: "Durum", render: r => <Badge level={STATUS_LEVEL[r.result?.status] || "inactive"}>{STATUS_LABEL[r.result?.status] || "?"}</Badge> },
          { label: "Hazırlık", render: r => <span className="font-mono">{(r.result?.readiness_pct ?? 0).toFixed(1)}%</span> },
          { label: "Tarih", render: r => (r.created_at || "").slice(0, 10) },
          { label: "İşlem", render: r => (
            <div onClick={e => e.stopPropagation()} className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => dlPdf(r)} data-testid={`pdf-${r.id}`} title="INCI Analiz PDF"><FileText size={12}/></Btn>
            </div>
          )},
        ]}
        rows={analyses}
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Uygunluk Analizi" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select label="Müşteri" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Formül *" value={f.formula_id} onChange={e => setF({ ...f, formula_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {formulas.map(fr => <option key={fr.id} value={fr.id}>{fr.code || fr.name} — {fr.product_name || ""}</option>)}
          </Select>
          <Input label="Revizyon (opsiyonel)" value={f.formula_revision} onChange={e => setF({ ...f, formula_revision: e.target.value })} placeholder="R01, R02, ..." />
          <Select label="Hedef Ülke *" value={f.country} onChange={e => setF({ ...f, country: e.target.value })}>
            {countries.map(c => <option key={c.code} value={c.code}>{c.name_tr} ({c.code})</option>)}
          </Select>
          <Select label="Rapor Dili" value={f.language} onChange={e => setF({ ...f, language: e.target.value })}>
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </Select>
          <Select label="AI Danışman (opsiyonel)" value={f.ai_model} onChange={e => setF({ ...f, ai_model: e.target.value })} data-testid="ai-model-select">
            <option value="">— AI kullanma —</option>
            {aiModels.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </Select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={analyze} disabled={busy} data-testid="run-analysis">{busy ? "Analiz ediliyor..." : "Analiz Et"}</Btn>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Analiz ${detail.analysis_no}` : ""} size="xl">
        {detail && <ComplianceDetail data={detail} onPdf={() => dlPdf(detail)} />}
      </Modal>
    </div>
  );
}

function ComplianceDetail({ data, onPdf }) {
  const r = data.result || {};
  const findings = r.findings || [];
  const statusLevel = STATUS_LEVEL[r.status] || "inactive";
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded">
        <div className="space-y-1">
          <div className="text-xs text-slate-500">Ürün / Formül</div>
          <div className="font-semibold">{data.formula_snapshot?.product_name || "—"} · {data.formula_snapshot?.formula_code} · {data.formula_snapshot?.revision}</div>
          <div className="text-xs text-slate-500">Hedef: {data.country}  ·  Müşteri: {data.customer?.legal_name || "—"}</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge level={statusLevel}>{STATUS_LABEL[r.status]}</Badge>
          <div className="text-lg font-bold font-mono">{(r.readiness_pct ?? 0).toFixed(1)}%</div>
          <Btn size="sm" onClick={onPdf} title="Regülasyon Analiz PDF"><FileText size={12}/></Btn>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={14}/> Bulgular ({findings.length})</div>
        <div className="space-y-2">
          {findings.map(f => (
            <div key={f.id} className={`p-2 rounded border ${
              f.status === "red" ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800" :
              f.status === "yellow" ? "bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800" :
              "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800"
            }`}>
              <div className="flex items-start gap-2">
                <span className="font-mono text-xs text-slate-500">{f.id}</span>
                <Badge level={STATUS_LEVEL[f.status]}>{STATUS_LABEL[f.status]}</Badge>
                <span className="font-semibold">{f.inci || f.rule_type}</span>
              </div>
              <div className="text-xs mt-1">{f.message_tr}</div>
              {f.recommendation_tr && <div className="text-xs mt-1 text-slate-600 dark:text-slate-400"><b>Öneri:</b> {f.recommendation_tr}</div>}
              <div className="text-[10px] text-slate-500 mt-1">Kaynak: {f.source?.authority || "—"} · {f.source?.regulation || ""} · {f.reference || ""}</div>
            </div>
          ))}
        </div>
      </div>
      {data.ai_advisory && (
        <div className={`p-3 rounded border ${data.ai_advisory.ok ? "bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800" : "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800"}`}>
          <div className="font-semibold text-sm mb-1 flex items-center gap-1"><Sparkles size={14}/> AI Danışman</div>
          {data.ai_advisory.ok
            ? <div className="text-xs whitespace-pre-line">{data.ai_advisory.text}</div>
            : <div className="text-xs text-amber-800 dark:text-amber-300">AI: {data.ai_advisory.error}</div>}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PIF PANE (Phase 2)
// ============================================================================
function PifPane() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formulas, setFormulas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [f, setF] = useState({ customer_id: "", formula_id: "", formula_revision: "",
                                target_market: "EU", language: "tr", regulatory_analysis_id: "" });

  const load = async () => {
    const [d, forms, custs, a] = await Promise.all([
      api.get("/regulatory/pif").catch(() => []),
      api.get("/formulas").catch(() => []),
      api.get("/customers").catch(() => []),
      api.get("/regulatory/analyses", { limit: 200 }).catch(() => []),
    ]);
    setDocs(d); setFormulas(forms); setCustomers(custs); setAnalyses(a);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.formula_id) { alert("Formül seçin"); return; }
    setBusy(true);
    try {
      const res = await api.post("/regulatory/pif", f);
      setDetail(res); setOpen(false); load();
    } catch (e) {
      alert(e?.response?.data?.detail || "PIF oluşturma hatası");
    } finally { setBusy(false); }
  };
  const openDetail = async (row) => setDetail(await api.get(`/regulatory/pif/${row.id}`));
  const dlPdf = async (row) => api.downloadFile(`/regulatory/pif/${row.id}/pdf`, `pif_${row.doc_no}.pdf`);

  return (
    <div>
      <div className="flex justify-between mb-3">
        <div className="text-sm text-slate-500">Ürün Bilgi Dosyası (PIF) — Regulation (EC) No 1223/2009 uyumlu yapılandırılmış dossier</div>
        <Btn onClick={() => setOpen(true)} data-testid="new-pif"><ClipboardCheck size={14}/> Yeni PIF Oluştur</Btn>
      </div>
      <Table testid="pif-list" onRowClick={openDetail}
        columns={[
          { label: "PIF No", render: r => <span className="font-mono text-xs">{r.doc_no}</span> },
          { label: "Ürün", render: r => (r.formula_snapshot?.product_name || "—") },
          { label: "Formül · Rev", render: r => `${r.formula_snapshot?.formula_code || "—"} · ${r.formula_snapshot?.revision || ""}` },
          { label: "Pazar", render: r => r.target_market || "—" },
          { label: "Durum", render: r => <Badge level={PIF_STATUS_LEVEL[r.status] || "inactive"}>{r.status}</Badge> },
          { label: "Bütünlük", render: r => <span className="font-mono">{(r.completeness?.pct ?? 0).toFixed(1)}%</span> },
          { label: "Blocking", render: r => <span className={(r.completeness?.blocking?.length || 0) > 0 ? "text-red-600 font-semibold" : "text-slate-400"}>{r.completeness?.blocking?.length || 0}</span> },
          { label: "Rev", render: r => `P${String(r.revision || 1).padStart(2, "0")}` },
          { label: "İşlem", render: r => (
            <div onClick={e => e.stopPropagation()} className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => dlPdf(r)} data-testid={`pif-pdf-${r.id}`} title="PIF PDF"><FileText size={12}/></Btn>
            </div>
          )},
        ]}
        rows={docs}
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Yeni PIF Oluştur" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select label="Müşteri" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Formül *" value={f.formula_id} onChange={e => setF({ ...f, formula_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {formulas.map(fr => <option key={fr.id} value={fr.id}>{fr.code || fr.name} — {fr.product_name || ""}</option>)}
          </Select>
          <Input label="Formül Revizyonu (opsiyonel)" value={f.formula_revision} onChange={e => setF({ ...f, formula_revision: e.target.value })} placeholder="R01, R02, ..." />
          <Select label="Hedef Pazar" value={f.target_market} onChange={e => setF({ ...f, target_market: e.target.value })}>
            <option value="EU">Avrupa Birliği (EU)</option>
            <option value="TR">Türkiye (TR)</option>
            <option value="UK">Birleşik Krallık (UK)</option>
            <option value="US">Amerika (US)</option>
            <option value="SA">Suudi Arabistan (SA)</option>
            <option value="AE">BAE (AE)</option>
          </Select>
          <Select label="Dil" value={f.language} onChange={e => setF({ ...f, language: e.target.value })}>
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </Select>
          <Select label="Mevzuat Analizi (Phase 1) bağla" value={f.regulatory_analysis_id} onChange={e => setF({ ...f, regulatory_analysis_id: e.target.value })}>
            <option value="">— Seçmezseniz PIF I. bölümü eksik olur —</option>
            {analyses.map(a => <option key={a.id} value={a.id}>{a.analysis_no} · {a.country} · {a.result?.status}</option>)}
          </Select>
        </div>
        <div className="mt-3 p-3 rounded bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 text-xs">
          <div className="flex items-start gap-2">
            <Sparkles size={14} className="mt-0.5 shrink-0"/>
            <div><b>AI Hallucination Firewall:</b> PIF assembler ASLA AI ile veri üretmez. Formül + malzeme + QC test + Phase 1 analizi verilerinden yapısal dossier oluşturur. Eksik alanlar "MISSING" olarak işaretlenir — hiçbir zaman uydurulmaz.</div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={create} disabled={busy} data-testid="run-pif-create">{busy ? "Hazırlanıyor..." : "PIF Oluştur"}</Btn>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `PIF ${detail.doc_no}` : ""} size="xl">
        {detail && <PifDetail data={detail} onPdf={() => dlPdf(detail)} onReload={async () => setDetail(await api.get(`/regulatory/pif/${detail.id}`))} onListReload={load} />}
      </Modal>
    </div>
  );
}

function PifDetail({ data, onPdf, onReload, onListReload }) {
  const [busy, setBusy] = useState(false);
  const c = data.completeness || {};
  const sections = data.sections || {};
  const canEdit = data.status === "DRAFT";
  const canReview = data.status === "DRAFT";
  const canApprove = data.status === "READY_FOR_REVIEW";
  const canRevise = data.status === "APPROVED";

  const changeStatus = async (target, allowOverride = false, reason = null) => {
    setBusy(true);
    try {
      await api.post(`/regulatory/pif/${data.id}/status`, { target, allow_override: allowOverride, override_reason: reason });
      await onReload(); await onListReload();
    } catch (e) {
      alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Durum değiştirilemedi");
    } finally { setBusy(false); }
  };
  const revise = async () => {
    setBusy(true);
    try {
      const nd = await api.post(`/regulatory/pif/${data.id}/revise`);
      alert(`Yeni revizyon oluşturuldu: ${nd.doc_no}`);
      await onListReload();
    } catch (e) { alert(e?.response?.data?.detail || "Revizyon oluşturulamadı"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-xs text-slate-500">Ürün · Formül · Rev</div>
          <div className="font-semibold">{data.formula_snapshot?.product_name} · {data.formula_snapshot?.formula_code} · {data.formula_snapshot?.revision}</div>
          <div className="text-xs text-slate-500">Pazar: {data.target_market} · Dil: {data.language} · PIF Rev: P{String(data.revision || 1).padStart(2, "0")}</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge level={PIF_STATUS_LEVEL[data.status]}>{data.status}</Badge>
          <div className="text-lg font-bold font-mono">{(c.pct ?? 0).toFixed(1)}%</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Btn size="sm" onClick={onPdf} data-testid="pif-detail-pdf" title="PIF PDF İndir"><FileText size={12}/></Btn>
        {canReview && <Btn size="sm" variant="secondary" disabled={busy} onClick={() => changeStatus("READY_FOR_REVIEW")} data-testid="pif-submit-review">İncelemeye Gönder</Btn>}
        {canApprove && <Btn size="sm" disabled={busy} onClick={() => changeStatus("APPROVED")} data-testid="pif-approve">Onayla</Btn>}
        {canRevise && <Btn size="sm" variant="secondary" disabled={busy} onClick={revise} data-testid="pif-revise">Yeni Revizyon</Btn>}
      </div>

      {/* Completeness */}
      <div className="p-3 border rounded border-slate-200 dark:border-slate-700">
        <div className="font-semibold mb-2 flex items-center gap-1"><Layers size={14}/> Dosya Bütünlüğü — {c.status}</div>
        {c.blocking?.length > 0 && (
          <div className="mb-2 p-2 rounded bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
            <div className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1 flex items-center gap-1"><XCircle size={12}/> Blocking ({c.blocking.length})</div>
            <ul className="text-xs list-disc list-inside space-y-0.5">
              {c.blocking.map((b, i) => <li key={i}><span className="font-mono text-[10px] mr-1">{b.section}</span>{b.label}</li>)}
            </ul>
          </div>
        )}
        {c.missing?.length > 0 && (
          <div className="mb-2 p-2 rounded bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
            <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1 flex items-center gap-1"><AlertCircle size={12}/> Eksik ({c.missing.length})</div>
            <ul className="text-xs list-disc list-inside space-y-0.5">
              {c.missing.slice(0, 20).map((m, i) => <li key={i}><span className="font-mono text-[10px] mr-1">{m.section}</span>{m.label}</li>)}
            </ul>
          </div>
        )}
        {c.ok?.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><CheckCircle2 size={12}/> Tamam ({c.ok.length})</summary>
            <ul className="mt-1 list-disc list-inside space-y-0.5">
              {c.ok.map((m, i) => <li key={i}><span className="font-mono text-[10px] mr-1">{m.section}</span>{m.label}</li>)}
            </ul>
          </details>
        )}
        <div className="text-[10px] text-slate-500 mt-2">
          Not: Yüzde skor tek başına yasal onay değildir. CPSR Bölüm B <b>yetkili güvenlik değerlendirici</b> imzası gerektirir.
        </div>
      </div>

      {/* Attachments — drag-drop, verifies K section counts */}
      <PifAttachmentsBlock docId={data.id} status={data.status} onChange={onReload} />

      {/* Supplier SDS extractions candidate list */}
      <PifExtractionsBlock docId={data.id} status={data.status} />

      {/* Sections summary */}
      <div className="space-y-2">
        <SectionCard title="A. Ürün Tanımı" data={sections.A_product} />
        <SectionCard title={`B. Formül (${sections.B_formula?.components?.length || 0} bileşen)`} data={{ components: sections.B_formula?.components?.slice(0, 5) }} />
        <SectionCard title={`C. Ham Madde (${sections.C_raw_materials?.records?.length || 0} kayıt)`} data={{ records: sections.C_raw_materials?.records?.slice(0, 5) }} />
        <SectionCard title="D. Üretim Yöntemi" data={{ status: sections.D_manufacturing?.status, batch_size_kg: sections.D_manufacturing?.batch_size_kg }} />
        <SectionCard title="E. GMP" data={sections.E_gmp} />
        <SectionCard title="F. Testler" data={{ tests: sections.F_safety_tests?.tests }} />
        <SectionCard title="G. Ambalaj" data={{ status: sections.G_packaging?.status }} />
        <SectionCard title="H. Etiket / Artwork" data={sections.H_label} />
        <SectionCard title="I. Mevzuat (Phase 1)" data={sections.I_regulatory} />
        <SectionCard title="J. CPSR (Safety Assessor)" data={{ status: sections.J_cpsr?.status, part_a_ok: !!sections.J_cpsr?.part_a?.product_info, part_b_signed: !!sections.J_cpsr?.part_b?.signature }} />
      </div>
    </div>
  );
}

// ============================================================================
// PIF EXTRACTIONS (supplier SDS candidate data)
// ============================================================================
function PifExtractionsBlock({ docId, status }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const canEdit = status !== "APPROVED" && status !== "SUPERSEDED";
  const load = React.useCallback(async () => {
    setItems(await api.get(`/regulatory/pif/${docId}/extractions`).catch(() => []));
  }, [docId]);
  useEffect(() => { load(); }, [load]);
  const verify = async (eid, components) => {
    try {
      await api.patch(`/regulatory/extractions/${eid}/verify`,
        { verified_components: components, notes: "Reviewed via UI" });
      await load();
      setActive(null);
      alert("Ekstrasyon doğrulandı. Malzeme kayıtları OTOMATİK değişmedi (spec §11).");
    } catch (e) { alert(e?.response?.data?.detail || "Doğrulama hatası"); }
  };
  if (items.length === 0) return null;
  return (
    <div className="p-3 border rounded border-slate-200 dark:border-slate-700">
      <div className="font-semibold mb-2 flex items-center gap-1 text-sm">
        <Sparkles size={14}/> Supplier SDS Ekstrasyonları ({items.length})
      </div>
      <div className="space-y-1">
        {items.map(e => (
          <div key={e.id} className="p-2 rounded bg-slate-50 dark:bg-slate-800/40 text-xs">
            <div className="flex items-center gap-2">
              <span className={`uppercase font-mono text-[9px] px-1.5 py-0.5 rounded ${e.verified ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"}`}>{e.verified ? "DOĞRULANDI" : "CANDIDATE"}</span>
              <span className="flex-1 truncate">{e.source_filename || "—"}</span>
              <span className="text-slate-400">{(e.candidate?.components?.length || 0)} bileşen</span>
              <Btn size="sm" variant="secondary" onClick={() => setActive(e)} data-testid={`ext-open-${e.id}`}>İncele</Btn>
            </div>
            {(e.warnings || []).length > 0 && (
              <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">Uyarılar: {e.warnings.join(", ")}</div>
            )}
          </div>
        ))}
      </div>
      <Modal open={!!active} onClose={() => setActive(null)} title={active ? `Ekstrasyon ${active.id?.slice(0,8)}` : ""} size="xl">
        {active && <ExtractionReviewer data={active} canEdit={canEdit} onVerify={verify} />}
      </Modal>
      <div className="text-[10px] text-slate-500 mt-2">
        Not: Extract sonuçları <b>candidate</b>'tır. Doğrulama malzeme kayıtlarını OTOMATİK MUTATE ETMEZ (spec §11).
      </div>
    </div>
  );
}

function ExtractionReviewer({ data, canEdit, onVerify }) {
  const [rows, setRows] = useState((data.verified_components || data.candidate?.components || []).map(c => ({...c})));
  return (
    <div className="space-y-3 text-xs">
      <div className="p-2 rounded bg-slate-100 dark:bg-slate-800">
        <div><b>Kaynak:</b> {data.source_filename || "—"} · {data.source_supplier_hint || "—"}</div>
        <div><b>Ekstraksiyon:</b> {data.extractor_version} · {(data.extracted_at || "").slice(0,19)}</div>
        <div><b>Ürün adı candidate:</b> {data.candidate?.product_identifier || "—"}</div>
        <div><b>Tedarikçi candidate:</b> {data.candidate?.supplier || "—"}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 dark:border-slate-700">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="p-1">CAS</th><th className="p-1">INCI</th><th className="p-1">Trade</th>
              <th className="p-1">% Aralık</th>
              {data.verified && canEdit && <th className="p-1">Merge</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-200 dark:border-slate-700">
                <td className="p-1 font-mono">{r.cas || "—"}</td>
                <td className="p-1"><input className="w-full px-1 py-0.5 bg-transparent border-b border-slate-300 dark:border-slate-600" value={r.inci || ""} onChange={e => setRows(rs => rs.map((x,j) => j===i ? {...x, inci: e.target.value} : x))} data-testid={`ext-inci-${i}`}/></td>
                <td className="p-1">{r.trade_name || "—"}</td>
                <td className="p-1 font-mono">{r.percent_range || "—"}</td>
                {data.verified && canEdit && (
                  <td className="p-1">
                    <Btn size="sm" variant="secondary" data-testid={`ext-merge-${i}`}
                      onClick={async () => {
                        const mid = window.prompt(
                          `Malzeme ID (mevcut malzemeyi güncellemek için) — boş bırakırsanız YENİ raw_material oluşturulur.\n\nCAS: ${r.cas}  ·  INCI: ${r.inci || "(boş)"}\n\nBoş bırak = yeni malzeme.`,
                          ""
                        );
                        if (mid === null) return;
                        try {
                          const res = await api.post(`/regulatory/extractions/${data.id}/merge-to-material`,
                            { component_index: i, target_material_id: mid || null,
                              overwrite_existing: false });
                          alert(`Merge başarılı: ${res.mode} · material_id=${res.material_id}`);
                        } catch (e) {
                          alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Merge hatası");
                        }
                      }}>Push</Btn>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={data.verified && canEdit ? 5 : 4} className="p-2 text-center text-slate-500">Bileşen bulunamadı — PDF taranmış/OCR gerekli olabilir</td></tr>}
          </tbody>
        </table>
      </div>
      {!data.verified && canEdit && (
        <div className="flex justify-end">
          <Btn onClick={() => onVerify(data.id, rows)} data-testid="ext-verify-btn">Doğrula ve Kaydet</Btn>
        </div>
      )}
      {data.verified && (
        <div className="text-emerald-700 dark:text-emerald-300"><b>Doğrulandı:</b> {data.verified_by_name} · {(data.verified_at || "").slice(0,19)}</div>
      )}
    </div>
  );
}

function SectionCard({ title, data }) {
  return (
    <details className="p-2 border rounded border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
      <summary className="cursor-pointer text-sm font-medium">{title}</summary>
      <pre className="mt-2 text-[10px] whitespace-pre-wrap text-slate-600 dark:text-slate-400 max-h-64 overflow-auto">{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}

// ============================================================================
// PIF ATTACHMENTS (upload/list/download/delete)
// ============================================================================
const ATTACHMENT_KINDS = [
  { key: "sds",          label: "Tedarikçi SDS" },
  { key: "spec",         label: "Spesifikasyon" },
  { key: "coa",          label: "CoA (Analiz Sertifikası)" },
  { key: "test_report",  label: "Test Raporu" },
  { key: "gmp_evidence", label: "GMP Kanıtı" },
  { key: "artwork",      label: "Artwork / Etiket" },
  { key: "cpsr",         label: "CPSR" },
  { key: "other",        label: "Diğer" },
];

function PifAttachmentsBlock({ docId, status, onChange }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("sds");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = React.useRef(null);
  const canEdit = status !== "APPROVED" && status !== "SUPERSEDED";

  const load = React.useCallback(async () => {
    setItems(await api.get(`/regulatory/pif/${docId}/attachments`).catch(() => []));
  }, [docId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file, k = kind) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", k);
      fd.append("label", file.name);
      fd.append("file", file);
      await api.postForm(`/regulatory/pif/${docId}/attachments`, fd);
      await load();
      if (onChange) await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Yükleme hatası");
    } finally { setUploading(false); }
  };
  const del = async (aid) => {
    if (!confirm("Bu ek silinsin mi?")) return;
    await api.del(`/regulatory/pif/${docId}/attachments/${aid}`);
    await load();
    if (onChange) await onChange();
  };
  const dl = async (att) => {
    await api.downloadFile(`/regulatory/pif/${docId}/attachments/${att.id}/download`, att.filename);
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!canEdit) return;
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach(f => upload(f));
  };

  return (
    <div className="p-3 border rounded border-slate-200 dark:border-slate-700">
      <div className="font-semibold mb-2 flex items-center gap-1 text-sm">
        <FileText size={14}/> Destekleyici Dokümanlar ({items.length})
      </div>
      {canEdit && (
        <div className="mb-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <Select value={kind} onChange={e => setKind(e.target.value)} data-testid="pif-attachment-kind">
            {ATTACHMENT_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
          </Select>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`flex-1 border-2 border-dashed rounded p-3 text-xs text-center cursor-pointer transition-colors ${
              dragOver ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30" :
              "border-slate-300 dark:border-slate-600"
            }`}
            data-testid="pif-attachment-drop"
          >
            {uploading ? "Yükleniyor..." : "Buraya sürükle-bırak veya tıkla · PDF/PNG/JPG/DOCX/XLSX · max 15 MB"}
          </div>
          <input ref={fileRef} type="file" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
            data-testid="pif-attachment-input"
          />
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-3">Henüz ek dosya yok. Tedarikçi SDS / artwork / CoA / spec yüklendikçe K bölümü indeksi güncellenir.</div>
      ) : (
        <div className="space-y-1">
          {items.map(a => (
            <div key={a.id} className="flex items-center gap-2 p-2 rounded bg-slate-50 dark:bg-slate-800/40 text-xs">
              <span className="uppercase font-mono text-[9px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200">{a.kind}</span>
              <span className="flex-1 truncate">{a.label || a.filename}</span>
              <span className="text-slate-400">{(a.size / 1024).toFixed(1)} KB</span>
              {a.kind === "sds" && a.content_type === "application/pdf" && canEdit && (
                <Btn size="sm" variant="secondary" onClick={async () => {
                  try {
                    await api.post(`/regulatory/pif/${docId}/attachments/${a.id}/extract-sds`);
                    alert("SDS ekstrasyonu tamamlandı. Extractions kısmında candidate'ı inceleyip doğrulayın.");
                  } catch (e) {
                    alert(e?.response?.data?.detail || "Extract hatası");
                  }
                }} data-testid={`att-extract-${a.id}`}>Extract SDS</Btn>
              )}
              <Btn size="sm" variant="secondary" onClick={() => dl(a)} data-testid={`att-dl-${a.id}`}>İndir</Btn>
              {canEdit && <Btn size="sm" variant="secondary" onClick={() => del(a.id)} data-testid={`att-del-${a.id}`}>Sil</Btn>}
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-500 mt-2">
        Not: Ek dosya eklemek PIF A-J bölüm içeriğini değiştirmez (immutable). Sadece K. destekleyici dokümanlar indeksi güncellenir.
      </div>
    </div>
  );
}

// ============================================================================
// SDS PANE (Phase 2)
// ============================================================================
function SdsPane() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formulas, setFormulas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: "", product_id: "", formula_id: "",
                                formula_revision: "", jurisdiction: "EU", language: "tr" });

  const load = async () => {
    const [d, forms, custs] = await Promise.all([
      api.get("/regulatory/sds").catch(() => []),
      api.get("/formulas").catch(() => []),
      api.get("/customers").catch(() => []),
    ]);
    setDocs(d); setFormulas(forms); setCustomers(custs);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.formula_id) { alert("Formül seçin"); return; }
    setBusy(true);
    try {
      const res = await api.post("/regulatory/sds", f);
      setDetail(res); setOpen(false); load();
    } catch (e) {
      alert(e?.response?.data?.detail || "SDS oluşturma hatası");
    } finally { setBusy(false); }
  };
  const openDetail = async (row) => setDetail(await api.get(`/regulatory/sds/${row.id}`));
  const dlPdf = async (row) => api.downloadFile(`/regulatory/sds/${row.id}/pdf`, `sds_${row.doc_no}.pdf`);

  return (
    <div>
      <div className="flex justify-between mb-3">
        <div className="text-sm text-slate-500">Safety Data Sheet (SDS) — 16 bölüm · CLP/GHS uyumlu · deterministik sınıflandırma çerçevesi</div>
        <Btn onClick={() => setOpen(true)} data-testid="new-sds"><Beaker size={14}/> Yeni SDS Oluştur</Btn>
      </div>
      <Table testid="sds-list" onRowClick={openDetail}
        columns={[
          { label: "SDS No", render: r => <span className="font-mono text-xs">{r.doc_no}</span> },
          { label: "Ürün", render: r => r.formula_snapshot?.product_name || "—" },
          { label: "Formül · Rev", render: r => `${r.formula_snapshot?.formula_code || "—"} · ${r.formula_snapshot?.revision || ""}` },
          { label: "Yargı Alanı", render: r => r.target_market || "—" },
          { label: "Dil", render: r => r.language?.toUpperCase() },
          { label: "Durum", render: r => <Badge level={PIF_STATUS_LEVEL[r.status] || "inactive"}>{r.status}</Badge> },
          { label: "Bilinen", render: r => <span className="font-mono">{(r.completeness?.pct ?? 0).toFixed(1)}%</span> },
          { label: "Blocking", render: r => <span className={(r.completeness?.blocking?.length || 0) > 0 ? "text-red-600 font-semibold" : "text-slate-400"}>{r.completeness?.blocking?.length || 0}</span> },
          { label: "İşlem", render: r => (
            <div onClick={e => e.stopPropagation()} className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => dlPdf(r)} data-testid={`sds-pdf-${r.id}`} title="SDS PDF"><FileText size={12}/></Btn>
            </div>
          )},
        ]}
        rows={docs}
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Yeni SDS Oluştur" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select label="Müşteri" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Formül *" value={f.formula_id} onChange={e => setF({ ...f, formula_id: e.target.value })}>
            <option value="">— Seçiniz —</option>
            {formulas.map(fr => <option key={fr.id} value={fr.id}>{fr.code || fr.name} — {fr.product_name || ""}</option>)}
          </Select>
          <Input label="Formül Revizyonu" value={f.formula_revision} onChange={e => setF({ ...f, formula_revision: e.target.value })} placeholder="R00, R01, ..." />
          <Select label="Yargı Alanı *" value={f.jurisdiction} onChange={e => setF({ ...f, jurisdiction: e.target.value })}>
            <option value="EU">Avrupa Birliği (EU)</option>
            <option value="TR">Türkiye (TR)</option>
            <option value="UK">Birleşik Krallık (UK)</option>
            <option value="US">Amerika (US)</option>
            <option value="SA">Suudi Arabistan (SA)</option>
            <option value="AE">BAE (AE)</option>
          </Select>
          <Select label="Dil *" value={f.language} onChange={e => setF({ ...f, language: e.target.value })}>
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </Select>
        </div>
        <div className="mt-3 p-3 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
            <div><b>UYARI:</b> CLP/GHS hazard sınıflandırması <b>yetkili uzman</b> tarafından ayrı hesaplanır. Sistem verified hazard-rule tabanı aktif değilken tüm sınıflandırmalar <b>MANUAL_REVIEW_REQUIRED</b> döner. AI ASLA authoritative classifier değildir. Transport (ADR/RID/IMDG/IATA) verified değilse <b>TRANSPORT REVIEW REQUIRED</b> işaretlenir.</div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={create} disabled={busy} data-testid="run-sds-create">{busy ? "Hazırlanıyor..." : "SDS Oluştur"}</Btn>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `SDS ${detail.doc_no}` : ""} size="xl">
        {detail && <SdsDetail data={detail} onPdf={() => dlPdf(detail)} onReload={async () => setDetail(await api.get(`/regulatory/sds/${detail.id}`))} onListReload={load} />}
      </Modal>
    </div>
  );
}

function SdsDetail({ data, onPdf, onReload, onListReload }) {
  const [busy, setBusy] = useState(false);
  const c = data.completeness || {};
  const sections = data.sections || {};
  const canReview = data.status === "DRAFT";
  const canApprove = data.status === "READY_FOR_REVIEW";
  const canRevise = data.status === "APPROVED";

  const changeStatus = async (target, allowOverride = false) => {
    setBusy(true);
    try {
      await api.post(`/regulatory/sds/${data.id}/status`, { target, allow_override: allowOverride });
      await onReload(); await onListReload();
    } catch (e) {
      alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Durum değiştirilemedi");
    } finally { setBusy(false); }
  };
  const revise = async () => {
    setBusy(true);
    try {
      const nd = await api.post(`/regulatory/sds/${data.id}/revise`);
      alert(`Yeni revizyon: ${nd.doc_no}`); await onListReload();
    } catch (e) { alert(e?.response?.data?.detail || "Revizyon oluşturulamadı"); }
    finally { setBusy(false); }
  };

  const SECTIONS = [
    ["s1_identification",     "1. Tanımlama"],
    ["s2_hazard",             "2. Tehlike"],
    ["s3_composition",        "3. Bileşim"],
    ["s4_first_aid",          "4. İlk Yardım"],
    ["s5_fire_fighting",      "5. Yangınla Mücadele"],
    ["s6_accidental_release", "6. Kaza Yayılımı"],
    ["s7_handling_storage",   "7. Kullanım/Depolama"],
    ["s8_exposure_controls",  "8. KKD"],
    ["s9_physical_chemical",  "9. Fiziksel/Kimyasal"],
    ["s10_stability",         "10. Kararlılık"],
    ["s11_toxicological",     "11. Toksikoloji"],
    ["s12_ecological",        "12. Ekoloji"],
    ["s13_disposal",          "13. Bertaraf"],
    ["s14_transport",         "14. Taşıma"],
    ["s15_regulatory",        "15. Mevzuat"],
    ["s16_other",             "16. Diğer"],
  ];
  return (
    <div className="space-y-4 text-sm">
      <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-xs text-slate-500">Ürün · Formül · Rev</div>
          <div className="font-semibold">{data.formula_snapshot?.product_name} · {data.formula_snapshot?.formula_code} · {data.formula_snapshot?.revision}</div>
          <div className="text-xs text-slate-500">Yargı: {data.target_market} · Dil: {data.language?.toUpperCase()} · SDS Rev: S{String(data.revision || 1).padStart(2, "0")}</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge level={PIF_STATUS_LEVEL[data.status]}>{data.status}</Badge>
          <div className="text-lg font-bold font-mono">{(c.pct ?? 0).toFixed(1)}%</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn size="sm" onClick={onPdf} data-testid="sds-detail-pdf" title="SDS PDF İndir"><FileText size={12}/></Btn>
        {canReview && <Btn size="sm" variant="secondary" disabled={busy} onClick={() => changeStatus("READY_FOR_REVIEW")} data-testid="sds-submit-review">İncelemeye Gönder</Btn>}
        {canApprove && <Btn size="sm" disabled={busy} onClick={() => changeStatus("APPROVED")} data-testid="sds-approve">Onayla</Btn>}
        {canRevise && <Btn size="sm" variant="secondary" disabled={busy} onClick={revise} data-testid="sds-revise">Yeni Revizyon</Btn>}
      </div>
      {sections.s2_hazard?.classification?.status === "MANUAL_REVIEW_REQUIRED" && (
        <div className="p-2 rounded bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800 text-xs text-red-800 dark:text-red-200">
          <b>UYARI:</b> Bölüm 2 hazard sınıflandırması yetkili uzman incelemesi gerektiriyor. AI/LLM authoritative classifier olarak kabul edilmez.
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 rounded bg-slate-100 dark:bg-slate-800"><b>Bilinen:</b> {c.known_count}</div>
        <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/30"><b>Missing/Review:</b> {(c.missing_count || 0) + (c.review_count || 0)}</div>
        <div className="p-2 rounded bg-red-100 dark:bg-red-900/30"><b>Blocking:</b> {c.blocking?.length || 0}</div>
      </div>
      <div className="space-y-2">
        {SECTIONS.map(([k, title]) => (
          <details key={k} className="p-2 border rounded border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
            <summary className="cursor-pointer text-sm font-medium">{title}</summary>
            <pre className="mt-2 text-[10px] whitespace-pre-wrap text-slate-600 dark:text-slate-400 max-h-64 overflow-auto">{JSON.stringify(sections[k], null, 2)}</pre>
          </details>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// SOURCES PANE
// ============================================================================
function SourcesPane() {
  const [sources, setSources] = useState([]);
  const [rules, setRules] = useState([]);
  const [hazardRules, setHazardRules] = useState([]);
  const [hazardQuery, setHazardQuery] = useState("");
  const [harmonizedOnly, setHarmonizedOnly] = useState(false);
  const [affectedRule, setAffectedRule] = useState(null);
  const [affected, setAffected] = useState(null);

  const loadHazard = React.useCallback(async () => {
    const params = {};
    if (hazardQuery) params.q = hazardQuery;
    if (harmonizedOnly) params.harmonized_only = true;
    setHazardRules(await api.get("/regulatory/hazard-rules", params).catch(() => []));
  }, [hazardQuery, harmonizedOnly]);
  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([
        api.get("/regulatory/sources").catch(() => []),
        api.get("/regulatory/rules").catch(() => []),
      ]);
      setSources(s); setRules(r);
    })();
  }, []);
  useEffect(() => { loadHazard(); }, [loadHazard]);

  const openAffected = async (rule) => {
    setAffectedRule(rule);
    setAffected(null);
    const data = await api.get(`/regulatory/hazard-rules/${rule.id}/affected-formulas`).catch(() => null);
    setAffected(data);
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="font-semibold mb-2 flex items-center gap-1"><ScrollText size={14}/> Resmi Ülke Kaynakları ({sources.length})</div>
        <Table testid="sources-table"
          columns={[
            { label: "Ülke", render: r => r.country },
            { label: "Otorite", render: r => r.authority },
            { label: "Mevzuat", render: r => r.regulation },
            { label: "Sürüm", render: r => r.version },
            { label: "URL", render: r => <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 underline text-xs">Bağlantı</a> },
          ]}
          rows={sources}
        />
      </div>
      <div>
        <div className="font-semibold mb-2 flex items-center gap-1"><BookOpenCheck size={14}/> Ülke Uygunluk Kuralları ({rules.length})</div>
        <Table testid="rules-table"
          columns={[
            { label: "Ülke", render: r => r.country },
            { label: "INCI", render: r => r.inci || "—" },
            { label: "CAS", render: r => r.cas || "—" },
            { label: "Tip", render: r => r.rule_type },
            { label: "Limit %", render: r => r.max_concentration_pct != null ? `${r.max_concentration_pct}%` : "—" },
            { label: "Referans", render: r => <span className="text-xs">{r.reference}</span> },
          ]}
          rows={rules}
        />
      </div>

      <div>
        <div className="font-semibold mb-2 flex items-center gap-1 justify-between">
          <span className="flex items-center gap-1"><AlertTriangle size={14}/> CLP Tehlike Kuralları (Hazard Rules · {hazardRules.length})</span>
          <div className="flex items-center gap-2">
            <Input placeholder="CAS / INCI / Kimyasal ara..." value={hazardQuery} onChange={e => setHazardQuery(e.target.value)} data-testid="hazard-search"/>
            <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
              <input type="checkbox" checked={harmonizedOnly} onChange={e => setHarmonizedOnly(e.target.checked)} data-testid="hazard-harmonized-only"/>
              Sadece harmonize (Annex VI)
            </label>
          </div>
        </div>
        <div className="text-[10px] text-slate-500 mb-1">Kaynak: CLP Regulation (EC) 1272/2008 Annex VI · ATP 21 (2025-04-01) + ECHA C&amp;L Inventory. Runtime kural ekleme YOK — sadece boot seed.</div>
        <Table testid="hazard-rules-table" onRowClick={openAffected}
          columns={[
            { label: "Kimyasal", render: r => <span className="font-semibold">{r.chem_name}</span> },
            { label: "CAS", render: r => <span className="font-mono text-xs">{r.cas}</span> },
            { label: "EC", render: r => <span className="font-mono text-xs">{r.ec || "—"}</span> },
            { label: "Index", render: r => <span className="font-mono text-[10px]">{r.index_no || "—"}</span> },
            { label: "Kaynak", render: r => r.harmonized
              ? <span className="text-xs text-emerald-700 dark:text-emerald-300">Harmonized</span>
              : <span className="text-xs text-amber-700 dark:text-amber-300">Self-class</span> },
            { label: "Sinyal", render: r => r.signal_word || "—" },
            { label: "Sınıflar", render: r => <span className="text-xs">{(r.hazard_classes || []).slice(0,3).join(", ")}{(r.hazard_classes || []).length > 3 ? "..." : ""}</span> },
            { label: "H", render: r => <span className="font-mono text-[10px]">{(r.h_statements || []).join(" ")}</span> },
            { label: "Sürüm", render: r => <span className="text-[10px] text-slate-500">{r.source?.version || "—"}</span> },
          ]}
          rows={hazardRules}
        />
      </div>

      <Modal open={!!affectedRule} onClose={() => { setAffectedRule(null); setAffected(null); }} title={affectedRule ? `${affectedRule.chem_name} — Etkilenen Formüller` : ""} size="xl">
        {affectedRule && (
          <div className="space-y-3 text-sm">
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/60 text-xs space-y-1">
              <div><b>CAS:</b> {affectedRule.cas} · <b>EC:</b> {affectedRule.ec || "—"} · <b>Index:</b> {affectedRule.index_no || "—"}</div>
              <div><b>Sınıflandırma:</b> {(affectedRule.hazard_classes || []).join(", ")}</div>
              <div><b>H-statements:</b> <span className="font-mono">{(affectedRule.h_statements || []).join(" ")}</span></div>
              <div><b>Kaynak:</b> {affectedRule.source?.authority} · {affectedRule.source?.regulation} · sürüm {affectedRule.source?.version} · yürürlük {affectedRule.source?.effective_date}</div>
              <div><a href={affectedRule.source?.source_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 underline">Resmi kaynak</a></div>
            </div>
            {!affected ? <div className="text-xs text-slate-500">Yükleniyor...</div> : (
              <>
                <div className="text-xs">
                  <b>Bu CAS'ı içeren malzemeler ({affected.materials?.length || 0}):</b>
                  {affected.materials?.length > 0 ? (
                    <ul className="mt-1 list-disc list-inside">
                      {affected.materials.map(m => <li key={m.id}><span className="font-mono text-[10px]">{m.code}</span> {m.name} · INCI: {m.inci_name || "—"}</li>)}
                    </ul>
                  ) : <span className="text-slate-500"> — yok</span>}
                </div>
                <div className="text-xs">
                  <b>Etkilenen aktif formüller ({affected.formulas?.length || 0}):</b>
                  {affected.formulas?.length > 0 ? (
                    <table className="w-full mt-1 border border-slate-200 dark:border-slate-700 text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr><th className="p-1 text-left">Kod</th><th className="p-1 text-left">Ürün</th><th className="p-1 text-left">Rev</th><th className="p-1 text-left">Durum</th></tr>
                      </thead>
                      <tbody>
                        {affected.formulas.map(f => (
                          <tr key={f.id} className="border-t border-slate-200 dark:border-slate-700">
                            <td className="p-1 font-mono">{f.code || f.id?.slice(0,8)}</td>
                            <td className="p-1">{f.product_name || "—"}</td>
                            <td className="p-1">{f.revision || "—"}</td>
                            <td className="p-1">{f.status || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <span className="text-slate-500"> — yok</span>}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}


// ============================================================================
// iter189 — Ürün Güvenlik Ön Değerlendirmesi (TASLAK / non-CPSR)
// ============================================================================
function SafetyPane() {
  const [profiles, setProfiles] = useState([]);
  const [methodology, setMethodology] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ formula_id: "", formula_revision: "",
    customer_id: "", product_id: "", product_category: "" });

  const load = async () => {
    const [pf, me, list, fo, cu] = await Promise.all([
      api.get("/safety/exposure-profiles").catch(() => []),
      api.get("/safety/methodology").catch(() => null),
      api.get("/safety/assessments", { limit: 200 }).catch(() => []),
      api.get("/formulas").catch(() => []),
      api.get("/customers").catch(() => []),
    ]);
    setProfiles(pf); setMethodology(me); setAssessments(list);
    setFormulas(fo); setCustomers(cu);
  };
  useEffect(() => { load(); }, []);

  const analyze = async () => {
    if (!f.formula_id) { alert("Formül seçin"); return; }
    if (!f.product_category) { alert("Ürün kategorisi seçin"); return; }
    setBusy(true);
    try {
      const res = await api.post("/safety/analyze", f);
      setDetail(res); setOpen(false); load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Değerlendirme hatası");
    } finally { setBusy(false); }
  };
  const dlPdf = async (row) => {
    await api.downloadFile(`/safety/assessments/${row.id}/pdf`,
      `safety_preliminary_${row.assessment_no}_TASLAK.pdf`);
  };
  const openDetail = async (row) => setDetail(await api.get(`/safety/assessments/${row.id}`));

  const statusBadge = (s) => (
    <Badge level={STATUS_LEVEL[s] || "inactive"}>{STATUS_LABEL[s] || "?"}</Badge>
  );

  return (
    <div data-testid="safety-pane">
      <div className="mb-3 p-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
        <b>Ürün Güvenlik Ön Değerlendirmesi — TASLAK.</b> Bu çıktı resmi
        Kozmetik Ürün Güvenlik Raporu (CPSR) değildir ve yetkili güvenlik
        değerlendiricisinin değerlendirmesinin yerine geçmez.
        {methodology && (
          <span className="ml-2 text-slate-600">
            Metodoloji: <span className="font-mono">{methodology.id}</span> — {methodology.version}
          </span>
        )}
      </div>
      <div className="flex justify-end mb-3">
        <Btn onClick={() => setOpen(true)} data-testid="new-safety-assessment">
          <Activity size={14}/> Ürün Güvenlik Ön Değerlendirmesi Hazırla
        </Btn>
      </div>
      <Table testid="safety-assessments" onRowClick={openDetail}
        columns={[
          { label: "Değerlendirme No", render: r => <span className="font-mono text-xs">{r.assessment_no}</span> },
          { label: "Ürün", render: r => r.product?.name || "—" },
          { label: "Kategori", render: r => r.product_category },
          { label: "Müşteri", render: r => r.customer?.legal_name || "—" },
          { label: "Metodoloji", render: r => <span className="font-mono text-xs">{r.methodology_id}</span> },
          { label: "Durum", render: r => statusBadge(r.result?.status) },
          { label: "Bileşen", render: r => {
            const t = r.result?.totals || {};
            return <span className="font-mono text-xs">
              <span className="text-emerald-700">{t.n_green || 0}</span>·
              <span className="text-amber-700">{t.n_yellow || 0}</span>·
              <span className="text-rose-700">{t.n_red || 0}</span>
            </span>;
          }},
          { label: "Tarih", render: r => (r.created_at || "").slice(0, 10) },
          { label: "İşlem", render: r => (
            <div onClick={e => e.stopPropagation()} className="flex gap-1">
              <Btn size="sm" variant="secondary" onClick={() => dlPdf(r)}
                   data-testid={`safety-pdf-${r.id}`} title="Güvenlik Değerlendirme PDF"><FileText size={12}/></Btn>
            </div>
          )},
        ]}
        rows={assessments}
      />

      <Modal open={open} onClose={() => setOpen(false)}
             title="Ürün Güvenlik Ön Değerlendirmesi Hazırla" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select label="Müşteri" value={f.customer_id}
                  onChange={e => setF({ ...f, customer_id: e.target.value })}
                  data-testid="safety-customer">
            <option value="">— Seçiniz —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Formül *" value={f.formula_id}
                  onChange={e => {
                    const fr = formulas.find(x => x.id === e.target.value);
                    setF({ ...f, formula_id: e.target.value,
                           formula_revision: fr?.revision || "",
                           product_id: fr?.product_id || "",
                           customer_id: f.customer_id || fr?.customer_id || "" });
                  }}
                  data-testid="safety-formula">
            <option value="">— Seçiniz —</option>
            {formulas.map(fr => <option key={fr.id} value={fr.id}>
              {fr.code || fr.name} — {fr.product_name || ""} · {fr.revision || "R00"}
            </option>)}
          </Select>
          <Select label="Ürün Kategorisi *" value={f.product_category}
                  onChange={e => setF({ ...f, product_category: e.target.value })}
                  data-testid="safety-category">
            <option value="">— Seçiniz —</option>
            {profiles.map(p => <option key={p.id} value={p.category}>{p.label_tr}</option>)}
          </Select>
          <Input label="Metodoloji"
                 value={methodology?.id || "SCCS-12TH-REV"} disabled/>
          <div className="col-span-full text-[11px] text-slate-500">
            Sistem otomatik olarak formülü + Hammadde Kartlarındaki doğrulanmış
            toksikoloji verisini + seçilen kategori maruziyet profilini kullanır.
            <b> AI çağrısı yapılmaz.</b>
          </div>
          <div className="col-span-full flex justify-end gap-2 mt-2">
            <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
            <Btn onClick={analyze} disabled={busy} data-testid="safety-run-btn">
              <Activity size={12}/> {busy ? "Hazırlanıyor..." : "Hazırla"}
            </Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)}
             title={detail ? `Ön Değerlendirme — ${detail.assessment_no} (TASLAK)` : ""}
             size="xl">
        {detail && (() => {
          const r = detail.result || {};
          const t = r.totals || {};
          const calcs = r.ingredient_calculations || [];
          const missing = r.missing_data || [];
          const warns = r.warnings || [];
          return (
            <div className="space-y-3 text-xs" data-testid="safety-detail">
              {/* iter226 — Revision-binding reassessment banner */}
              <SafetyBindingBanner assessmentId={detail.id} />
              <div className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                Bu çıktı resmi CPSR değildir. Nihai değerlendirme yetkin bir güvenlik değerlendiricisi tarafından yapılmalıdır.
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><b>Ürün:</b> {detail.product?.name || "—"}</div>
                <div><b>Kategori:</b> {detail.product_category}</div>
                <div><b>Formül:</b> {detail.formula_snapshot?.formula_code} · {detail.formula_snapshot?.revision}</div>
                <div><b>Metodoloji:</b> <span className="font-mono">{detail.methodology_id}</span></div>
              </div>
              <div className="inline-block px-3 py-1 rounded-full ring-1 text-sm font-semibold"
                   data-testid="safety-overall-status">
                Genel: {statusBadge(r.status)}
                <span className="ml-2 text-[11px] text-slate-500">
                  Yeşil {t.n_green || 0} · Sarı {t.n_yellow || 0} · Kırmızı {t.n_red || 0}
                  {t.n_insufficient_data ? ` · Eksik veri ${t.n_insufficient_data}` : ""}
                </span>
              </div>
              {calcs.length > 0 && (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr><th className="p-1.5 text-left">INCI</th>
                          <th className="p-1.5 text-right">%</th>
                          <th className="p-1.5 text-right">NOAEL</th>
                          <th className="p-1.5 text-right">DAp %</th>
                          <th className="p-1.5 text-right">SED</th>
                          <th className="p-1.5 text-right">MoS</th>
                          <th className="p-1.5 text-left">Durum</th>
                          <th className="p-1.5 text-left">Kaynak</th></tr>
                    </thead>
                    <tbody>
                      {calcs.map((c, i) => (
                        <tr key={i} className="border-t" data-testid={`safety-calc-${i}`}>
                          <td className="p-1.5">{c.inci}</td>
                          <td className="p-1.5 text-right font-mono">%{Number(c.concentration_pct || 0).toFixed(3)}</td>
                          <td className="p-1.5 text-right font-mono">
                            {c.noael != null ? `${c.noael} ${c.noael_unit || ""}` : "—"}
                          </td>
                          <td className="p-1.5 text-right font-mono">
                            {c.dermal_absorption_pct != null ? `%${c.dermal_absorption_pct}` : "—"}
                          </td>
                          <td className="p-1.5 text-right font-mono">{c.sed != null ? Number(c.sed).toFixed(4) : "—"}</td>
                          <td className="p-1.5 text-right font-mono">{c.mos != null ? c.mos : "—"}</td>
                          <td className="p-1.5">{statusBadge(c.status)}</td>
                          <td className="p-1.5 text-[10px] text-slate-500">
                            {c.tox_source?.source_title || c.reason || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {missing.length > 0 && (
                <div className="p-2 rounded bg-amber-50 border border-amber-200"
                     data-testid="safety-missing">
                  <div className="font-semibold mb-1">🟡 Eksik Veri</div>
                  <ul className="list-disc list-inside">
                    {missing.map((m, i) => (
                      <li key={i}>
                        <b>{m.material_name || m.inci}</b> — {m.issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {warns.length > 0 && (
                <div className="p-2 rounded bg-amber-50 border border-amber-200">
                  <div className="font-semibold mb-1">Uyarılar</div>
                  <ul className="list-disc list-inside">
                    {warns.map((w, i) => (
                      <li key={i}><b>{w.code}</b>: {w.message_tr}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Btn variant="secondary" onClick={() => dlPdf(detail)}
                     data-testid="safety-detail-pdf" title="Güvenlik Değerlendirme PDF Al">
                  <FileText size={12}/>
                </Btn>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

// ============================================================================
// iter191 — Mevzuat Dataset Editörü (admin only)
// ============================================================================
function DatasetEditorPane() {
  const [sub, setSub] = useState("ifra");
  const [ifraRules, setIfraRules] = useState([]);
  const [allergenRules, setAllergenRules] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [modal, setModal] = useState(null); // { type, data, isNew }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [ifra, alg, map] = await Promise.all([
      api.get("/regulatory/ifra-rules").catch(() => []),
      api.get("/regulatory/allergen-rules").catch(() => []),
      api.get("/regulatory/ifra-category-mapping").catch(() => []),
    ]);
    setIfraRules(ifra); setAllergenRules(alg); setMapping(map);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal) return;
    const { type, data, isNew } = modal;
    if (!data.source_url && !data.source_reference) {
      alert("En az bir kaynak alanı zorunludur: Source URL veya Source Reference.");
      return;
    }
    setBusy(true);
    try {
      const path = type === "ifra" ? "/regulatory/ifra-rules"
        : type === "allergen" ? "/regulatory/allergen-rules"
        : "/regulatory/ifra-category-mapping";
      if (isNew) await api.post(path, data);
      else await api.put(`${path}/${data.id}`, data);
      setModal(null); load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const del = async (type, id) => {
    if (!window.confirm("Bu kayıt soft-delete edilecek. Emin misiniz?")) return;
    const path = type === "ifra" ? "/regulatory/ifra-rules"
      : type === "allergen" ? "/regulatory/allergen-rules"
      : "/regulatory/ifra-category-mapping";
    await api.delete(`${path}/${id}`);
    load();
  };

  return (
    <div data-testid="dataset-editor-pane">
      <div className="mb-3 p-3 rounded bg-indigo-50 border border-indigo-200 text-xs text-indigo-900">
        <b>Mevzuat Dataset Editörü.</b> Sadece admin. Her mutasyon audit_logs'a yazılır.
        Her kayıt için <b>source_url</b> veya <b>source_reference</b> zorunludur.
        Soft-delete: mevcut analiz snapshot'larını değiştirmez.
      </div>
      <div className="flex gap-2 mb-3">
        {[["ifra", `IFRA Kuralları (${ifraRules.length})`],
          ["allergen", `Allergen Kuralları (${allergenRules.length})`],
          ["mapping", `Kategori Eşleme (${mapping.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)}
                  data-testid={`dataset-subtab-${k}`}
                  className={`px-3 py-1 rounded text-sm ${sub === k ? "bg-indigo-600 text-white" : "bg-slate-100"}`}>{l}</button>
        ))}
        <div className="grow"/>
        <Btn size="sm" onClick={() => {
          const empty = sub === "ifra"
            ? { ingredient_inci: "", ifra_category: "cat_05a",
                restriction_type: "max_level", max_level_pct: 0,
                amendment: "51st Amendment", dataset_version: "IFRA-A51-2023",
                source_url: "", source_reference: "", verified: true }
            : sub === "allergen"
            ? { entry_no: null, inci_name: "", cas: "", grouped: false,
                component_incis: [], jurisdiction: "EU",
                regulation_reference: "Commission Regulation (EU) 2023/1545",
                annex_reference: "Annex III",
                leave_on_threshold_pct: 0.001, rinse_off_threshold_pct: 0.01,
                requirement_type: "label_required",
                dataset_version: "EU-2023-1545",
                source_url: "", source_reference: "", verified: true }
            : { product_category: "", ifra_category: "cat_05a",
                source_url: "", source_reference: "", verified: true };
          setModal({ type: sub, data: empty, isNew: true });
        }} data-testid="dataset-add-btn"><Plus size={12}/> Yeni</Btn>
      </div>

      {sub === "ifra" && (
        <Table testid="ifra-rules-table" rows={ifraRules} columns={[
          { label: "ID", render: r => <span className="font-mono text-xs">{r.id}</span> },
          { label: "INCI", render: r => r.ingredient_inci },
          { label: "CAS", render: r => r.ingredient_cas || "—" },
          { label: "Kategori", render: r => r.ifra_category },
          { label: "Limit %", render: r => r.max_level_pct != null ? `%${r.max_level_pct}` : "—" },
          { label: "Amendment", render: r => r.amendment || "—" },
          { label: "Kaynak", render: r => r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">Aç</a> : r.source_reference || "—" },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary"
                   onClick={() => setModal({ type: "ifra", data: { ...r }, isNew: false })}
                   data-testid={`ifra-edit-${r.id}`}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={() => del("ifra", r.id)}
                   data-testid={`ifra-del-${r.id}`}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]}/>
      )}
      {sub === "allergen" && (
        <Table testid="allergen-rules-table" rows={allergenRules} columns={[
          { label: "#", render: r => r.entry_no || "—" },
          { label: "INCI", render: r => r.inci_name },
          { label: "CAS", render: r => r.cas || "—" },
          { label: "Leave-on %", render: r => r.leave_on_threshold_pct != null ? `%${r.leave_on_threshold_pct}` : "—" },
          { label: "Rinse-off %", render: r => r.rinse_off_threshold_pct != null ? `%${r.rinse_off_threshold_pct}` : "—" },
          { label: "Grup", render: r => r.grouped ? "✓" : "—" },
          { label: "Kaynak", render: r => r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">Aç</a> : r.source_reference || "—" },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary"
                   onClick={() => setModal({ type: "allergen", data: { ...r }, isNew: false })}
                   data-testid={`alg-edit-${r.id}`}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={() => del("allergen", r.id)}
                   data-testid={`alg-del-${r.id}`}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]}/>
      )}
      {sub === "mapping" && (
        <Table testid="mapping-table" rows={mapping} columns={[
          { label: "Ürün Kategorisi", render: r => r.product_category },
          { label: "IFRA Kategori", render: r => r.ifra_category },
          { label: "Kaynak", render: r => r.source_reference || (r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">Aç</a> : "—") },
          { label: "İşlem", render: r => (
            <div className="flex gap-1">
              <Btn size="sm" variant="secondary"
                   onClick={() => setModal({ type: "mapping", data: { ...r }, isNew: false })}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={() => del("mapping", r.id)}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]}/>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)}
             title={modal ? `${modal.isNew ? "Yeni" : "Güncelle"} — ${modal.type.toUpperCase()}` : ""} size="xl">
        {modal && (() => {
          const d = modal.data;
          const set = (patch) => setModal({ ...modal, data: { ...d, ...patch } });
          return (
            <div className="grid grid-cols-2 gap-3">
              {modal.type === "ifra" && (<>
                <Input label="INCI *" value={d.ingredient_inci || ""} onChange={e => set({ ingredient_inci: e.target.value })} data-testid="dsm-ifra-inci"/>
                <Input label="CAS" value={d.ingredient_cas || ""} onChange={e => set({ ingredient_cas: e.target.value })}/>
                <Input label="IFRA Kategori *" value={d.ifra_category || ""} onChange={e => set({ ifra_category: e.target.value })}/>
                <Input label="Max Level (%)" type="number" step="0.0001" value={d.max_level_pct ?? ""} onChange={e => set({ max_level_pct: parseFloat(e.target.value) || 0 })}/>
                <Input label="Amendment" value={d.amendment || ""} onChange={e => set({ amendment: e.target.value })}/>
                <Input label="Dataset Version" value={d.dataset_version || ""} onChange={e => set({ dataset_version: e.target.value })}/>
              </>)}
              {modal.type === "allergen" && (<>
                <Input label="Annex III Entry No" type="number" value={d.entry_no ?? ""} onChange={e => set({ entry_no: parseInt(e.target.value) || null })}/>
                <Input label="INCI *" value={d.inci_name || ""} onChange={e => set({ inci_name: e.target.value })}/>
                <Input label="CAS" value={d.cas || ""} onChange={e => set({ cas: e.target.value })}/>
                <Input label="Leave-on Threshold (%)" type="number" step="0.00001" value={d.leave_on_threshold_pct ?? ""} onChange={e => set({ leave_on_threshold_pct: parseFloat(e.target.value) })}/>
                <Input label="Rinse-off Threshold (%)" type="number" step="0.00001" value={d.rinse_off_threshold_pct ?? ""} onChange={e => set({ rinse_off_threshold_pct: parseFloat(e.target.value) })}/>
                <Input label="Regulation Reference" value={d.regulation_reference || ""} onChange={e => set({ regulation_reference: e.target.value })}/>
                <Input label="Dataset Version" value={d.dataset_version || ""} onChange={e => set({ dataset_version: e.target.value })}/>
              </>)}
              {modal.type === "mapping" && (<>
                <Input label="Ürün Kategorisi *" value={d.product_category || ""} onChange={e => set({ product_category: e.target.value })}/>
                <Input label="IFRA Kategori *" value={d.ifra_category || ""} onChange={e => set({ ifra_category: e.target.value })}/>
              </>)}
              <Input label="Source URL" value={d.source_url || ""} onChange={e => set({ source_url: e.target.value })} data-testid="dsm-source-url"/>
              <Input label="Source Reference" value={d.source_reference || ""} onChange={e => set({ source_reference: e.target.value })}/>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!d.verified} onChange={e => set({ verified: e.target.checked })} data-testid="dsm-verified"/>
                <span className={d.verified ? "text-emerald-700 font-semibold" : "text-slate-500"}>Doğrulandı (verified)</span>
              </label>
              <div className="col-span-2 text-[11px] text-slate-500">
                <b>Zorunlu:</b> Source URL veya Source Reference alanlarından en az biri doldurulmalıdır. Verified doğrulama audit_logs'a kaydedilir.
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn>
                <Btn onClick={save} disabled={busy} data-testid="dsm-save-btn">
                  <Save size={12}/> {busy ? "Kaydediliyor..." : "Kaydet"}
                </Btn>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

